import React from 'react';

/**
 * Impersonation banner — shown whenever `impersonating_handle` is set in
 * localStorage. Only rendered inside AppShell so admin's session is visually
 * distinct. Exit button restores the admin's stashed real token.
 */
export default function ImpersonationBanner() {
  const handle = typeof window !== 'undefined' ? window.localStorage.getItem('impersonating_handle') : null;
  if (!handle) return null;

  const exit = async () => {
    const TOKEN_KEY = 'clanster_access_token';
    const backup = window.localStorage.getItem('admin_backup_token');
    if (!backup) {
      // Nothing to restore — send admin to login. Clear ALL storage so
      // there's no stale state left when they sign back in.
      window.localStorage.removeItem('impersonating_user_id');
      window.localStorage.removeItem('impersonating_handle');
      window.localStorage.removeItem('impersonating_readonly');
      window.location.href = '/login';
      return;
    }
    // Ask the backend to swap the httponly cookie back to the admin
    // identity. We pass the admin token BOTH in the body (endpoint
    // contract) AND in the Authorization header (defence-in-depth so
    // cookie-samesite quirks on some prod domains don't accidentally
    // starve the middleware of auth). We also refuse to swallow the
    // response — a hard error surfaces to the user so they know to
    // retry or sign back in as admin, rather than sitting on a broken
    // impersonated screen forever.
    let backendOk = false;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/seed/impersonate/exit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${backup}`,
        },
        credentials: 'include',
        body: JSON.stringify({ admin_backup_token: backup }),
      });
      backendOk = res.ok;
      if (!res.ok) {
        // Log for the browser console so support can diagnose from user
        // devtools if this ever surfaces on prod again.
         
        console.error('[impersonation] /impersonate/exit returned', res.status);
      }
    } catch (err) {
       
      console.error('[impersonation] /impersonate/exit network failure:', err);
    }
    // Restore local auth state UNCONDITIONALLY — even if the backend
    // call failed, at least the client-side surface stops rendering the
    // impersonation banner. Worst case the admin has to sign in fresh.
    // Clear BOTH sessionStorage (sandbox path) AND localStorage (admin
    // panel path) so no stale token can be picked up by api.js on the
    // next request (api.js reads sessionStorage first).
    try { window.sessionStorage.removeItem(TOKEN_KEY); } catch (_) { /* storage blocked */ }
    try { window.sessionStorage.removeItem('sandbox_handle'); } catch (_) { /* storage blocked */ }
    window.localStorage.setItem(TOKEN_KEY, backup);
    window.localStorage.removeItem('admin_backup_token');
    window.localStorage.removeItem('impersonating_user_id');
    window.localStorage.removeItem('impersonating_handle');
    window.localStorage.removeItem('impersonating_readonly');
    if (!backendOk) {
      // Backend didn't confirm the cookie swap. Warn the admin — they
      // may need to sign back in if the cookie is still stuck on the
      // impersonated identity.
       
      window.alert('Exit impersonation completed locally, but the server did not confirm the session swap. If you continue to see the impersonated view after landing, sign out and back in as admin.');
    }
    // Force a HARD navigation with replace() so this URL leaves the
    // history stack (browser Back can't accidentally revive the
    // impersonated state) and no SPA route interceptor can re-render
    // the banner off the stale state that was just cleared.
    window.location.replace('/admin/seed');
  };

  const readonly = typeof window !== 'undefined' && window.localStorage.getItem('impersonating_readonly') === '1';

  return (
    <div className={`impersonation-banner${readonly ? ' is-readonly' : ''}`} role="alert" data-testid="impersonation-banner">
      <span className="ib-icon" aria-hidden="true">{readonly ? '🎭' : '⚠️'}</span>
      <span className="ib-text">
        {readonly ? (
          <>Viewing as <b>@{handle}</b> — <b>read-only mode</b>. Any write action will return 403.</>
        ) : (
          <>Viewing as <b>@{handle}</b> — actions here are attributed to this seed account.</>
        )}
      </span>
      <button className="ib-exit" onClick={exit} data-testid="impersonation-exit">
        Exit impersonation
      </button>
    </div>
  );
}
